import { Calendar as BigCalendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { Calendar as PrimeCalendar } from "primereact/calendar";

const localizer = momentLocalizer(moment);

// 👇 Custom component for rendering events in month/week/day view
function CustomEvent({ event }) {
  return (
    <div className="calendar-content">
      <strong>{event.title}</strong>
      {/* {event.message && <div style={{ fontSize: "0.75em", color: "#fff" }}>{event.message.length > 30 ? event.message.slice(0, 30) + "..." : event.message}</div>} */}
      {event.email && <div style={{ fontSize: "0.7em", color: "#fff" }}>Email: {event.email}</div>}
      {event.phone && <div style={{ fontSize: "0.7em", color: "#fff" }}>Phone: {event.phone}</div>}
    </div>
  );
}

// 👇 Custom agenda event (for Agenda view)
function CustomAgendaEvent({ event }) {
  return (
    <span>
      <strong>{event.title}</strong>
      <br />
      {event.message && <em>{event.message}</em>}
      {event.email && <div>Email: {event.email}</div>}
      {event.phone && <div>Phone: {event.phone}</div>}
    </span>
  );
}

function CustomToolbar({ label, onNavigate, onView, view, onCreateEvent }) {
  return (
    <div className="rbc-toolbar d-flex justify-content-between align-items-center">
      {/* Left Controls: Navigation */}
      <div className="rbc-btn-group rbc-btn-group-left">
        <button className="btn-today custom-today-button" onClick={() => onNavigate("TODAY")}>
          Today
        </button>
        <button className="btn-back custom-back-button" onClick={() => onNavigate("PREV")}>
          <i className="ri-arrow-left-s-line"></i>
        </button>

        <button className="btn-next custom-next-button" onClick={() => onNavigate("NEXT")}>
          <i className="ri-arrow-right-s-line"></i>
        </button>
        <span className="rbc-toolbar-label">{label}</span>
      </div>

      {/* Right Controls: View Switcher */}
      <div className="rbc-btn-group rbc-btn-group-right">
        <button type="button" className="create-event-btn" onClick={onCreateEvent}>
          <i className="ri-add-line"></i>&nbsp;Create
        </button>
        {view === "agenda" ? (
          " "
        ) : (
          <select className="form-select custom-view-select" value={view} onChange={(e) => onView(e.target.value)}>
            <option value="month">Month</option>
            <option value="week">Week</option>
            <option value="day">Day</option>
          </select>
        )}

        <div className="grid-view">
          <button type="button" className={`${view === "agenda" ? "btn" : "btn btn-month-active"}`} onClick={() => onView("month")}>
            <i className="ri-calendar-event-line"></i>
          </button>
          <button type="button" className={`${view !== "agenda" ? "btn" : "btn btn-agenda-active"}`} onClick={() => onView("agenda")}>
            <i className="ri-list-check-3"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EventCalendar({ events = [] }) {
  const [date, setDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState(Views.MONTH);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const popupRef = useRef(null);
  const [myEvents, setMyEvents] = useState(events);
  const [popupVisible, setPopupVisible] = useState(false);
  const [readyToShow, setReadyToShow] = useState(false);
  const [popupStyle, setPopupStyle] = useState({ top: 0, left: 0 });

  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [newEventData, setNewEventData] = useState({
    title: "",
    message: "",
    email: "",
    phone: "",
    start: new Date(),
    end: moment().add(1, "hour").toDate(), // default 1-hour event
  });
  const handleSelectSlot = useCallback(({ start, end, action }) => {
    setNewEventData({ start, end });
    setShowCreatePopup(true);
  }, []);

  // useEffect(() => {
  //   if (selectedEvent && modalRef.current) {
  //     const modal = new window.bootstrap.Modal(modalRef.current);
  //     modal.show();
  //   }
  // }, [selectedEvent]);

  useEffect(() => {
    if (popupVisible && readyToShow && popupRef.current) {
      setTimeout(() => {
        const popupRect = popupRef.current.getBoundingClientRect();
        const padding = 0;
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        // Use the last mouse click (or bounding box) position
        const anchorX = popupStyle.left === -9999 ? 0 : popupStyle.left;
        const anchorY = popupStyle.top === -9999 ? 0 : popupStyle.top;

        let top = anchorY + padding;
        let left = anchorX + padding;

        if (left + popupRect.width > screenWidth) {
          left = screenWidth - popupRect.width - padding;
        }

        if (top + popupRect.height > screenHeight) {
          top = screenHeight - popupRect.height - padding;
        }

        if (left < padding) {
          left = padding;
        }

        if (top < padding) {
          top = padding;
        }

        setPopupStyle({ top, left });
        setReadyToShow(false); // done measuring
      }, 200);
    }
  }, [popupVisible, readyToShow]);

  return (
    <>
      <BigCalendar
        date={date}
        localizer={localizer}
        events={myEvents}
        startAccessor="start"
        endAccessor="end"
        view={calendarView}
        onNavigate={(newDate) => setDate(newDate)}
        onView={(view) => setCalendarView(view)}
        onSelectEvent={(event, e) => {
          setSelectedEvent(event);
          setPopupStyle({
            top: e.clientY, // temporary placeholder
            left: e.clientX,
          });
          setPopupVisible(true); // triggers rendering
          setReadyToShow(true);
        }}
        // onSelectSlot={handleSelectSlot}
        popup
        selectable
        onSelectSlot={handleSelectSlot}
        components={{
          event: CustomEvent,
          toolbar: (toolbarProps) => (
            <CustomToolbar
              {...toolbarProps}
              onCreateEvent={() => {
                setNewEventData({
                  title: "",
                  message: "",
                  email: "",
                  phone: "",
                  start: new Date(),
                  end: moment().add(1, "hour").toDate(),
                });
                setShowCreatePopup(true);
              }}
            />
          ),
          agenda: {
            event: CustomAgendaEvent,
          },
        }}
      />
      {showCreatePopup && (
        <div className="custom-event-popup custom-event-popup-new">
          <div className="popup-head">
            {/* <h5>Create New Event</h5> */}
            <button onClick={() => setShowCreatePopup(false)}>
              <i className="ri-close-line"></i>
            </button>
          </div>
          <div className="popup-body">
            <div className="rbc-event-text rbc-event-text-main">
              <input className="input-large" type="text" placeholder="Event Title" value={newEventData.title} onChange={(e) => setNewEventData({ ...newEventData, title: e.target.value })} />
              <nav>
                <div className="event-tab" id="nav-tab" role="tablist">
                  <button className="nav-link" id="nav-home-tab" data-bs-toggle="tab" data-bs-target="#nav-home" type="button" role="tab" aria-controls="nav-home" aria-selected="false">
                    Event
                  </button>
                  <button className="nav-link active" id="nav-profile-tab" data-bs-toggle="tab" data-bs-target="#nav-profile" type="button" role="tab" aria-controls="nav-profile" aria-selected="true">
                    Task
                  </button>
                </div>
              </nav>
            </div>

            <div className="tab-content" id="nav-tabContent">
              <div className="tab-pane fade" id="nav-home" role="tabpanel" aria-labelledby="nav-home-tab">
                <div className="rbc-event-text rcb-event-control">
                  <i className="ri-time-line"></i>
                  Need Content
                </div>
              </div>
              <div className="tab-pane fade active show" id="nav-profile" role="tabpanel" aria-labelledby="nav-profile-tab">
                <div className="rbc-event-text rcb-event-control">
                  <i className="ri-time-line"></i>
                  <div>
                    <PrimeCalendar
                      id="newLeadCalendar"
                      showTime
                      hourFormat="12"
                      value={newEventData.start}
                      onChange={(e) =>
                        setNewEventData({
                          ...newEventData,
                          start: e.target.value,
                        })
                      }
                      dateFormat="MM d, yy"
                    />
                  </div>
                </div>
                <div className="rbc-event-text rcb-event-control">
                  <i className="ri-bar-chart-horizontal-line"></i>
                  <textarea
                    placeholder="Description"
                    value={newEventData.message}
                    onChange={(e) =>
                      setNewEventData({
                        ...newEventData,
                        message: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="tabpane-footer">
                  <button
                    className="event-btn"
                    onClick={() => {
                      if (newEventData.title) {
                        setMyEvents((prev) => [...prev, newEventData]);
                        setShowCreatePopup(false);
                      } else {
                        alert("Please enter a title.");
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="popup-foot"></div>
        </div>
      )}

      {selectedEvent && popupVisible && (
        <div
          ref={popupRef}
          className="custom-event-popup"
          style={{
            top: popupStyle.top,
            left: popupStyle.left,
            visibility: popupVisible ? "visible" : "hidden",
          }}
        >
          <div className="popup-head">
            <button>
              <i className="ri-delete-bin-line"></i>
            </button>
            <button>
              <i className="ri-more-2-fill"></i>
            </button>
            <button
              onClick={() => {
                setSelectedEvent(null);
                setPopupVisible(false);
              }}
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
          <div className="popup-body">
            <div className="rbc-event-title">
              <i className="ri-square-fill"></i>
              <h4>
                {selectedEvent.title}
                <span>
                  {moment(selectedEvent.start).format("dddd, MMMM DD")} &nbsp;- &nbsp;
                  {moment(selectedEvent.start).format("hh:mm A")}
                </span>
              </h4>
            </div>
            {selectedEvent.email && (
              <p className="rbc-event-text">
                <i class="ri-mail-line"></i>
                {selectedEvent.email}
              </p>
            )}
            {selectedEvent.phone && (
              <p className="rbc-event-text">
                <i class="ri-phone-line"></i>
                {selectedEvent.phone}
              </p>
            )}
            {selectedEvent.message && (
              <p className="rbc-event-text">
                <i className="ri-bar-chart-horizontal-line"></i>
                {selectedEvent.message}
              </p>
            )}
            <p className="rbc-event-text">
              <i className="ri-notification-3-line"></i> Notifications
            </p>
            <p className="rbc-event-text">
              <i className="ri-shield-user-line"></i> Alice Pare
            </p>
          </div>
          <div className="popup-foot">
            <h6>Going?</h6>
            <div>
              <button type="button">Yes</button>
              <button type="button">No</button>
              <button type="button">Maybe</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
